import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Calendar, TrendingUp, FileText, LogOut, 
  Plus, Edit, Trash2, Eye, Search, Filter,
  ChevronDown, Mail, Phone, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countryFlags = {
  DE: '🇩🇪', NL: '🇳🇱', IT: '🇮🇹', FR: '🇫🇷', PL: '🇵🇱',
  ES: '🇪🇸', PT: '🇵🇹', BE: '🇧🇪', SE: '🇸🇪', FI: '🇫🇮'
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem('admin_token', response.data.token);
      onLogin(response.data.token);
      toast.success('Login successful!');
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFBF8]">
      <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#002A76]">Admin Login</h1>
          <p className="text-gray-600 text-sm mt-2">TieClad Dashboard</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@tieclad.com"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          <Button 
            type="submit" 
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full"
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
        <p className="text-xs text-gray-500 text-center mt-4">
          Default: admin@tieclad.com / tieclad2024
        </p>
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ token, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadFilter, setLeadFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/leads/stats`, authHeaders),
        axios.get(`${API}/leads`, authHeaders),
        axios.get(`${API}/sessions`)
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { status }, authHeaders);
      setLeads(leads.map(l => l.id === leadId ? { ...l, status } : l));
      toast.success('Lead status updated');
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;
    try {
      await axios.delete(`${API}/sessions/${sessionId}`, authHeaders);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Session deleted');
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesFilter = leadFilter === 'all' || lead.status === leadFilter;
    const matchesSearch = lead.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#002A76] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FFFBF8]">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-[#002A76]">TieClad Admin</h1>
          <Button 
            variant="outline" 
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="rounded-full"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Stats */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#002A76]/10 flex items-center justify-center">
                  <Users size={20} className="text-[#002A76]" />
                </div>
                <span className="text-gray-600 text-sm">Total Leads</span>
              </div>
              <p className="text-3xl font-bold text-[#002A76]">{stats?.total || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#0AA06A]/10 flex items-center justify-center">
                  <TrendingUp size={20} className="text-[#0AA06A]" />
                </div>
                <span className="text-gray-600 text-sm">New Leads</span>
              </div>
              <p className="text-3xl font-bold text-[#0AA06A]">{stats?.by_status?.new || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#F59E0B]/10 flex items-center justify-center">
                  <Calendar size={20} className="text-[#F59E0B]" />
                </div>
                <span className="text-gray-600 text-sm">Active Sessions</span>
              </div>
              <p className="text-3xl font-bold text-[#F59E0B]">{sessions.length}</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
                  <FileText size={20} className="text-[#3B82F6]" />
                </div>
                <span className="text-gray-600 text-sm">Converted</span>
              </div>
              <p className="text-3xl font-bold text-[#3B82F6]">{stats?.by_status?.converted || 0}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="leads">
            <TabsList className="bg-white border rounded-full p-1 w-full max-w-sm">
              <TabsTrigger 
                value="leads" 
                data-testid="tab-leads"
                className="rounded-full data-[state=active]:bg-[#002A76] data-[state=active]:text-white flex-1"
              >
                Leads ({leads.length})
              </TabsTrigger>
              <TabsTrigger 
                value="sessions"
                data-testid="tab-sessions"
                className="rounded-full data-[state=active]:bg-[#002A76] data-[state=active]:text-white flex-1"
              >
                Sessions ({sessions.length})
              </TabsTrigger>
            </TabsList>

            {/* Leads Tab */}
            <TabsContent value="leads" className="mt-6">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    data-testid="lead-search"
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={leadFilter} onValueChange={setLeadFilter}>
                  <SelectTrigger data-testid="lead-filter" className="w-full md:w-48">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Leads Table */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F7F4F0]">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-[#002A76]">Name</th>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-[#002A76]">Contact</th>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-[#002A76]">Goal</th>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-[#002A76]">Source</th>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-[#002A76]">Status</th>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-[#002A76]">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.map((lead) => (
                        <tr key={lead.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-[#002A76]">{lead.full_name}</p>
                            <p className="text-xs text-gray-500">{lead.qualification}</p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#002A76]">
                                <Mail size={12} /> {lead.email}
                              </a>
                              <a href={`tel:${lead.whatsapp}`} className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#002A76]">
                                <Phone size={12} /> {lead.whatsapp}
                              </a>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm">{lead.goal}</p>
                            <p className="text-xs text-gray-500">{lead.country_interest}</p>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs">
                              {lead.source?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Select 
                              value={lead.status} 
                              onValueChange={(value) => updateLeadStatus(lead.id, value)}
                            >
                              <SelectTrigger className="h-8 w-28 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="qualified">Qualified</SelectItem>
                                <SelectItem value="converted">Converted</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(lead.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredLeads.length === 0 && (
                  <div className="py-12 text-center text-gray-500">
                    No leads found
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Sessions Tab */}
            <TabsContent value="sessions" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sessions.map((session) => (
                  <div key={session.id} className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{countryFlags[session.country_code]}</span>
                        <Badge className={session.session_type === 'free_session' ? 'badge-free text-white' : 'badge-paid text-white'}>
                          {session.session_type === 'free_session' ? 'Free' : `₹${session.price}`}
                        </Badge>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => deleteSession(session.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    <h3 className="font-semibold text-[#002A76] mb-2 line-clamp-2">{session.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(session.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {session.time}
                      </span>
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        <Users size={14} className="inline mr-1" />
                        {session.seats_filled}/{session.max_seats}
                      </span>
                      <a 
                        href={`/session/${session.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#002A76] hover:text-[#0AA06A]"
                      >
                        <Eye size={16} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

// Main Admin Page
export const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  if (!token) {
    return <AdminLogin onLogin={setToken} />;
  }

  return <Dashboard token={token} onLogout={handleLogout} />;
};

export default AdminPage;
